function parseSvgLength(value, { basis = 100, fallback = 0 } = {}) {
  if (typeof value === "number" && Number.isFinite(value)) return value;

  const stringValue = String(value ?? "").trim();
  if (stringValue === "") return fallback;

  if (stringValue.endsWith("%")) {
    const parsed = Number.parseFloat(stringValue);
    return Number.isFinite(parsed) ? (parsed / 100) * basis : fallback;
  }

  const parsed = Number.parseFloat(stringValue);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function parseFontWidth(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value;

  const parsed = Number.parseFloat(String(value ?? "").trim());
  return Number.isFinite(parsed) ? parsed : 100;
}

function parseFontWeight(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value;

  const parsed = Number.parseFloat(String(value ?? "").trim());
  return Number.isFinite(parsed) ? parsed : 400;
}

function parseLetterSpacing(value, fontSize) {
  if (typeof value === "number" && Number.isFinite(value)) return value;

  const stringValue = String(value ?? "").trim();
  if (stringValue === "") return 0;

  if (stringValue.endsWith("em")) {
    const parsed = Number.parseFloat(stringValue);
    return Number.isFinite(parsed) ? parsed * fontSize : 0;
  }

  const parsed = Number.parseFloat(stringValue);
  return Number.isFinite(parsed) ? parsed : 0;
}

function transformCommands(commands, { translateX = 0 }) {
  return commands.map((command) => {
    const nextCommand = { ...command };

    for (const suffix of ["", "1", "2"]) {
      const xKey = `x${suffix}`;

      if (Number.isFinite(nextCommand[xKey])) {
        nextCommand[xKey] += translateX;
      }
    }

    return nextCommand;
  });
}

function escapeSvgAttribute(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

// Shared by browser map badges and server badge exports.
export function createRouteBadgeTextRenderer(font, routeBadgeViewBox) {
  return function renderText({
    label,
    fill,
    x,
    y,
    fontSize,
    fontWeight,
    fontWidth,
    fontStretch,
    letterSpacing,
  }) {
    const resolvedFontSize = Number(fontSize) || 50;
    const targetX = parseSvgLength(x, {
      basis: routeBadgeViewBox.width,
      fallback: routeBadgeViewBox.width / 2,
    });
    const targetY = parseSvgLength(y, {
      basis: routeBadgeViewBox.height,
      fallback: routeBadgeViewBox.height / 2,
    });
    const widthPercent = parseFontWidth(fontWidth ?? fontStretch);
    const weight = parseFontWeight(fontWeight);
    const textOptions = {
      kerning: true,
      letterSpacing:
        parseLetterSpacing(letterSpacing, resolvedFontSize) /
        resolvedFontSize,
      variation: {
        wght: weight,
        wdth: widthPercent,
      },
    };
    const textPath = font.getPath(
      String(label ?? ""),
      0,
      targetY,
      resolvedFontSize,
      textOptions,
    );
    const advanceWidth = font.getAdvanceWidth(
      String(label ?? ""),
      resolvedFontSize,
      textOptions,
    );
    const translateX = targetX - advanceWidth / 2;

    textPath.commands = transformCommands(textPath.commands, {
      translateX,
    });

    const escapedFill = escapeSvgAttribute(fill);

    return `<path fill="${escapedFill}" d="${textPath.toPathData(2)}"/>`;
  };
}
